const POLL_MS = 3 * 60 * 1000;
const VERSION_URL = '/version.txt';

function parseVersion(text: string): number | null {
  const n = parseInt(text.trim(), 10);
  return Number.isFinite(n) ? n : null;
}

async function fetchVersion(): Promise<number | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return parseVersion(await res.text());
  } catch {
    return null;
  }
}

/**
 * Reads /version.txt on load (current build), then polls for a newer number.
 * Returns a disposer for HMR / teardown.
 */
export function startVersionCheck(): () => void {
  const label = document.getElementById('build-version');
  const nudge = document.getElementById('update-nudge');
  const refresh = document.getElementById('update-refresh');
  if (!label || !nudge) return () => {};

  let current = 0;
  let haveCurrent = false;
  let disposed = false;

  const showUpdate = (remote: number) => {
    nudge.classList.add('visible');
    nudge.setAttribute('aria-hidden', 'false');
    const tipVer = nudge.querySelector('.update-nudge-remote');
    if (tipVer) tipVer.textContent = `v${remote}`;
  };

  const check = async () => {
    if (disposed) return;
    const remote = await fetchVersion();
    if (remote == null || disposed) return;
    if (!haveCurrent) {
      haveCurrent = true;
      current = remote;
      label.textContent = `v${remote}`;
      return;
    }
    if (remote > current) showUpdate(remote);
  };

  const reload = (e?: Event) => {
    e?.preventDefault();
    window.location.reload();
  };
  refresh?.addEventListener('click', reload);
  const icon = nudge.querySelector('.update-nudge-icon');
  icon?.addEventListener('click', reload);

  const onVisible = () => {
    if (document.visibilityState === 'visible') void check();
  };
  document.addEventListener('visibilitychange', onVisible);

  void check();
  const timer = window.setInterval(() => void check(), POLL_MS);

  return () => {
    disposed = true;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    refresh?.removeEventListener('click', reload);
    icon?.removeEventListener('click', reload);
  };
}
